import { isEmpty } from '@sgme/fp';
import classNames from 'classnames';
import { AmountSkeleton } from 'components/shared/Amount/Amount';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import type { IntlKey } from 'locales';
import { uniqBy } from 'lodash-es';
import type { Item } from 'models/IItem';
import { type ChangeEvent, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { camelCaseToWords } from 'utils/string';

interface OwnProps<T extends string> {
  id: string;
  value: string;
  e2e?: string;
  translationKey: IntlKey;
  availableItems: ReadonlyArray<Item<T>>;
  isInvalid?: boolean;
  isWarning?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  onValueChange: (newValue: T) => void;
}

export function ListField<T extends string>({
  id,
  value,
  e2e,
  availableItems,
  translationKey,
  isInvalid = false,
  isWarning = false,
  isLoading = false,
  disabled = false,
  onValueChange,
}: OwnProps<T>) {
  const { formatMessage } = useIntl();
  const onChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const newValue = e.target.value;
      if (newValue !== value) {
        onValueChange(newValue as T);
      }
    },
    [value, onValueChange],
  );

  const availableOptions = useMemo(() => {
    const sanitized = uniqBy(availableItems, 'value');
    if (sanitized.length < availableItems.length) {
      throw new Error(
        `ListField<${id}> contains some bad values (duplicate) : ${JSON.stringify(availableItems.map(x => x.value))}`,
      );
    }

    return sanitized.map(({ label, value: val }) => {
      if (isEmpty(label)) {
        const lab = val ?? '';
        return { value: val, label: lab };
      }
      return {
        value: val,
        label: formatMessage({ id: `field.${label}` as IntlKey, defaultMessage: camelCaseToWords(label) }),
      };
    });
  }, [availableItems, formatMessage, id]);

  const isReadOnly = availableOptions.length === 1;
  return (
    <BlockOrientationFormGroup inputId={id} label={translationKey}>
      {isLoading && <AmountSkeleton />}
      {!isLoading && (
        <select
          id={id}
          className={classNames('form-select', { 'is-invalid': isInvalid, 'is-warning': isWarning })}
          value={value || ''}
          onChange={onChange}
          disabled={isReadOnly || disabled}
          data-e2e={e2e || `${id}-select`}
        >
          {availableOptions.map(({ value: itemValue, label: itemLabel }) => (
            <option key={itemValue || 'null'} value={itemValue}>
              {itemValue === ''
                ? ''
                : itemLabel === ''
                  ? ''
                  : formatMessage({ id: `form.${id}${itemValue}` as IntlKey, defaultMessage: itemLabel })}
            </option>
          ))}
        </select>
      )}
    </BlockOrientationFormGroup>
  );
}

ListField.displayName = 'ListField';
